import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import SearchableFilter from "@/components/SearchableFilter";

const PILL_COLORS = ["bg-[#D3E5EF] text-[#183B56]","bg-[#E8DEEE] text-[#492B61]","bg-[#FDECC8] text-[#6B4C13]","bg-[#DBEDDB] text-[#1E4620]","bg-[#FFE2DD] text-[#6E3630]","bg-[#F5E0C3] text-[#5B3F14]","bg-[#D3D0F5] text-[#2E2B7A]","bg-[#F0D9E5] text-[#6B2D50]"];
function pillColor(s) { let h=0; for(let i=0;i<(s||"").length;i++) h=s.charCodeAt(i)+((h<<5)-h); return PILL_COLORS[Math.abs(h)%PILL_COLORS.length]; }

export default function RosterImport() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const target = searchParams.get("target") || "weekly";
  const weekStart = searchParams.get("week_start") || "";
  const date = searchParams.get("date") || "";
  const source = searchParams.get("source") || "all";

  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [goalFilter, setGoalFilter] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        if (source === "weekly" && target === "daily") {
          const ws = weekStart || getMonday(date);
          const r = await API.get("/roster?week_start=" + ws);
          const items = r.data.items || [];
          setTasks(items.map(i => i.task).filter(t => t?.task_id));
        } else {
          const r = await API.get("/tasks/flat");
          setTasks(r.data.filter(t => t.status !== "done"));
        }
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [source, target, weekStart, date]);

  function getMonday(ds) { const d = new Date(ds + "T12:00:00"); d.setDate(d.getDate() - d.getDay() + (d.getDay()===0?-6:1)); return [d.getFullYear(),String(d.getMonth()+1).padStart(2,'0'),String(d.getDate()).padStart(2,'0')].join('-'); }

  const toggleTask = (tid) => { const n = new Set(selectedTasks); n.has(tid)?n.delete(tid):n.add(tid); setSelectedTasks(n); };

  const handleSubmit = async () => {
    const items = Array.from(selectedTasks).map(tid => ({ task_id: tid }));
    if (!items.length) { toast.error("Select at least one task"); return; }
    try {
      if (target === "weekly") { await API.post("/roster/batch", { week_start: weekStart, items }); toast.success("Added to weekly roster"); navigate("/weekly-roster"); }
      else { await API.post("/daily-roster/batch", { date, items }); toast.success("Added to daily roster"); navigate("/daily-roster"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const quickCreate = async () => {
    if (!newTitle.trim()) return;
    try { const r = await API.post("/tasks", { title: newTitle }); setTasks(prev => [r.data, ...prev]); const n = new Set(selectedTasks); n.add(r.data.task_id); setSelectedTasks(n); setNewTitle(""); setShowQuickCreate(false); toast.success("Task created and selected"); }
    catch { toast.error("Failed"); }
  };

  const goals = [...new Map(tasks.filter(t => t.goal_title && t.goal_title !== "Independent").map(t => [t.goal_id, { value: t.goal_id, label: t.goal_title }])).values()];
  const projects = [...new Map(tasks.filter(t => t.project_title && t.project_title !== "Independent").map(t => [t.project_id, { value: t.project_id, label: t.project_title }])).values()];

  const filtered = tasks.filter(t => {
    if (search && !t.title?.toLowerCase().includes(search.toLowerCase())) return false;
    if (goalFilter.length > 0 && !goalFilter.includes(t.goal_id)) return false;
    if (projectFilter.length > 0 && !projectFilter.includes(t.project_id)) return false;
    return true;
  });

  const backPath = target === "weekly" ? "/weekly-roster" : "/daily-roster";
  const title = target === "weekly" ? "Import to Weekly Roster" : source === "weekly" ? "Import from Weekly Roster" : "Import to Daily Roster";

  return (
    <div data-testid="roster-import-page" className="max-w-[1000px]">
      <button onClick={() => navigate(backPath)} className="flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#37352F] mb-6 transition-colors" data-testid="back-from-import"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-start justify-between mb-4">
        <div><h1 className="text-[28px] font-bold text-[#37352F]">{title}</h1><p className="text-[13px] text-[#6B6B6B] mt-0.5">Select tasks to add</p></div>
        <div className="flex gap-2">
          <Button onClick={() => setShowQuickCreate(!showQuickCreate)} variant="ghost" size="sm" className="text-[#6B6B6B] text-[12px] h-7"><Plus className="w-3.5 h-3.5 mr-1" /> Create Task</Button>
          <Button onClick={handleSubmit} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8" data-testid="confirm-import">Add {selectedTasks.size} task(s)</Button>
        </div>
      </div>

      {showQuickCreate && (
        <div className="mb-3 p-3 border border-[#E0E0DE] rounded-[4px] bg-[#F7F6F3]">
          <div className="flex gap-2"><Input value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key==="Enter" && quickCreate()} placeholder="Task title..." className="bg-white border-[#E0E0DE] text-[14px]" data-testid="import-new-task-input" />
          <Button onClick={quickCreate} size="sm" className="bg-[#2383E2] text-white text-[13px] h-9">Create</Button></div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-[280px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A0]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..."
            className="w-full pl-8 pr-3 py-[5px] text-[13px] border border-[#E0E0DE] rounded-[4px] bg-transparent outline-none focus:border-[#2383E2]/50 text-[#37352F] placeholder:text-[#A3A3A0]" data-testid="import-search" />
        </div>
        <SearchableFilter label="Goal" items={goals} values={goalFilter} onChange={setGoalFilter} />
        <SearchableFilter label="Project" items={projects} values={projectFilter} onChange={setProjectFilter} />
      </div>

      {loading ? <p className="text-[#6B6B6B] italic text-center py-8">Loading...</p> : filtered.length === 0 ? <p className="text-[#6B6B6B] italic text-center py-12">No tasks available</p> : (
        <div className="space-y-px">
          {filtered.map(task => (
            <div key={task.task_id} onClick={() => toggleTask(task.task_id)}
              className={"flex items-center gap-3 py-2.5 px-3 rounded-[4px] cursor-pointer transition-colors " + (selectedTasks.has(task.task_id) ? "bg-[#F0F7FF]" : "hover:bg-[#F7F6F3]")}
              data-testid={"import-task-" + task.task_id}>
              <Checkbox checked={selectedTasks.has(task.task_id)} onCheckedChange={() => toggleTask(task.task_id)}
                className="h-[16px] w-[16px] rounded-[3px] border-[#B8B6B0] data-[state=checked]:bg-[#2383E2] data-[state=checked]:border-[#2383E2]" />
              <span className="text-[14px] text-[#37352F] font-medium flex-1">{task.title}</span>
              {task.project_title && task.project_title !== "Independent" && <span className={"text-[11px] px-2 py-0.5 rounded-full font-medium " + pillColor(task.project_title)}>{task.project_title}</span>}
              {task.goal_title && task.goal_title !== "Independent" && <span className={"text-[11px] px-2 py-0.5 rounded-full font-medium " + pillColor(task.goal_title)}>{task.goal_title}</span>}
            </div>
          ))}
        </div>
      )}

      {selectedTasks.size > 0 && (
        <div className="sticky bottom-4 mt-6 p-3 border border-[#E0E0DE] rounded-[4px] bg-white/95 backdrop-blur-sm flex items-center justify-between">
          <span className="text-[13px] text-[#6B6B6B]">{selectedTasks.size} task(s) selected</span>
          <Button onClick={handleSubmit} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8" data-testid="bottom-confirm-import">Add to Roster</Button>
        </div>
      )}
    </div>
  );
}
